import React from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Icon } from '@/nova/components';
import { Sheet, useSheetDismiss } from '@/nova/components/Sheet';
import { useAppSelector } from '@/store';

import { gameKey, gameTitle, resolveGamesByIds, type GameSourceRecord } from '../../game-sources';
import { fetchGamesByIds } from '../../games-api';
import { useGameSources } from '../../useGameSources';
import { GameThumb } from '../GameThumb';
import * as S from './GamePickerSheet.styles';

/** How many games to render before the "Load more" button (the All catalogue is huge, so it is windowed). */
const PAGE = 60;

export interface GamePickerSheetProps {
  /** The chosen games (multi-select, in click order) once the owner confirms, as FULL catalogue records (art bundle
   *  included), so the taker can draw their key art. */
  onConfirm: (games: GameSourceRecord[]) => void;
  /** Dismiss without choosing (backdrop / Escape / Cancel). */
  onClose: () => void;
  /** Game ids to pre-select (editing an existing ad): resolved against the catalogue and checked on open,
   *  so the owner can add/remove from the current games rather than starting over. */
  initialSelectedIds?: string[];
  /** Cap on the selection: unselected cards are disabled once this many are picked (mirrors the composer's
   *  promotion cap, so confirm never silently drops picks the draft can't hold). */
  max?: number;
}

/**
 * Pick the game(s) a marketing asset advertises, from the tenant-0 global catalogue: every game, found by the search
 * bar and windowed (thousands of games; no Popular / Top shortlists). Selection is multi-select (keyed by game id).
 * Confirm hands the chosen games up; the composer then seeds its draft from them.
 *
 * The catalogue is the SLIM one (no art): each card draws its art read by id (GameThumb), and confirm reads the chosen
 * games' full records the same way before handing them up.
 */
export const GamePickerSheet = ({
  onConfirm,
  onClose,
  initialSelectedIds,
  max,
}: GamePickerSheetProps): React.ReactElement => {
  const { t } = useTranslation();
  const { closeHandle } = useSheetDismiss(onClose);
  // The brand the chosen games' full records are read for (raw value, coerced outside the selector: see useGameSources).
  const brandTenant = useAppSelector((state) => state.casinoInstance.displayBrand?.tenant_id);
  const tenantRaw = Number(brandTenant);
  const tenantId = Number.isFinite(tenantRaw) && tenantRaw > 0 ? tenantRaw : undefined;
  const [query, setQuery] = React.useState('');
  const [visible, setVisible] = React.useState(PAGE);
  const [selected, setSelected] = React.useState<Record<string, GameSourceRecord>>({});
  // Try again reads the catalogue again after a read that failed.
  const [catalogAttempt, setCatalogAttempt] = React.useState(0);
  const { games, catalog, loading, failed } = useGameSources('all', true, catalogAttempt);
  // Confirm reads the chosen games' full records first: while it does the button waits (the ref holds a second press
  // off before the state lands), and a read that failed is said in the footer until the next press or pick.
  const [confirming, setConfirming] = React.useState(false);
  const [confirmFailed, setConfirmFailed] = React.useState(false);
  const confirmingRef = React.useRef(false);
  // A read that answers after the sheet has gone hands nothing up.
  const mountedRef = React.useRef(true);
  React.useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Reset the window on a new search.
  React.useEffect(() => setVisible(PAGE), [query]);

  // Pre-select the ad's already-chosen games ONCE the catalogue resolves (it may arrive after mount on a
  // deep-link reopen). Merge under any picks the owner already made (`...prev` wins) so a click before the
  // catalogue lands is never wiped, and seed only once so a later deselect is not undone.
  const seededRef = React.useRef(false);
  React.useEffect(() => {
    if (seededRef.current) return;
    if (!initialSelectedIds || initialSelectedIds.length === 0) {
      seededRef.current = true;
      return;
    }
    if (catalog.length === 0) return; // wait for the catalogue before resolving ids
    const seeded = Object.fromEntries(resolveGamesByIds(initialSelectedIds, catalog).map((g) => [gameKey(g), g]));
    setSelected((prev) => ({ ...seeded, ...prev }));
    seededRef.current = true;
  }, [initialSelectedIds, catalog]);

  const filtered = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return games;
    return games.filter(
      (g) =>
        gameTitle(g).toLowerCase().includes(q) ||
        gameKey(g).includes(q) ||
        String(g.provider_id ?? '').includes(q),
    );
  }, [games, query]);

  const shown = filtered.slice(0, visible);
  const selectedList = Object.values(selected);
  const atMax = max != null && selectedList.length >= max;

  const toggle = (game: GameSourceRecord): void => {
    setConfirmFailed(false);
    setSelected((prev) => {
      const key = gameKey(game);
      const next = { ...prev };
      if (next[key]) delete next[key];
      // Self-gate the cap (styling alone does not block a click): never add past `max`.
      else if (!(max != null && Object.keys(prev).length >= max)) next[key] = game;
      return next;
    });
  };

  /** Hands the chosen games up as FULL records: the taker draws their key art (gameKeyArtUrl), which a slim catalogue
   *  record does not carry. A record that came with an art bundle is a full one already (a spark from before the slim
   *  view answers the catalogue whole) and is not read again; any other is read by id, usually from the cache its card's
   *  art already filled. A game the read does not return goes up as it is. A read that fails hands nothing up. */
  const confirm = (): void => {
    if (confirmingRef.current || selectedList.length === 0 || failed) return;
    const picks = selectedList;
    const toRead = picks.filter((game) => game.art_bundle === undefined).map(gameKey);
    if (toRead.length === 0 || tenantId === undefined) {
      onConfirm(picks);
      return;
    }
    confirmingRef.current = true;
    setConfirming(true);
    setConfirmFailed(false);
    fetchGamesByIds(tenantId, toRead).then(
      (full) => {
        confirmingRef.current = false;
        if (!mountedRef.current) return;
        setConfirming(false);
        onConfirm(picks.map((game) => full.get(gameKey(game)) ?? game));
      },
      () => {
        confirmingRef.current = false;
        if (!mountedRef.current) return;
        setConfirming(false);
        setConfirmFailed(true);
      },
    );
  };

  return (
    <Sheet
      title="Choose games to advertise"
      onClose={onClose}
      closeHandle={closeHandle}
      maxWidth="920px"
      footer={
        <S.FooterRow>
          {/* A choice whose games could not be read is said where the owner is looking (in place of the count). */}
          {confirmFailed ? (
            <span role="alert">
              <S.SelCount text="We could not load this. Try again in a moment." />
            </span>
          ) : (
            <S.SelCount text="{{n}} selected" placeholders={{ n: selectedList.length }} />
          )}
          <S.FooterActions>
            <Button variant="text" label={t('Cancel')} onClick={onClose} />
            {/* Held while the catalogue could not be read: the games already chosen are resolved from it, and a
                confirm then would hand up a selection without them. Held too while the chosen games are read. */}
            <Button
              variant="primary"
              label={t('Use selected games')}
              onClick={confirm}
              disabled={selectedList.length === 0 || failed || confirming}
            />
          </S.FooterActions>
        </S.FooterRow>
      }
    >
      <S.Toolbar>
        <S.Search
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t('Search games')}
          aria-label={t('Search games')}
        />
      </S.Toolbar>

      {loading && filtered.length === 0 ? <S.Note text="Loading games…" /> : null}
      {/* A catalogue that could not be read is not one with no games: it says so, with a way to read it again. */}
      {!loading && filtered.length === 0 ? (
        failed ? (
          <>
            <S.Note text="We could not load this. Try again in a moment." />
            <S.LoadMoreRow>
              <Button variant="outline" label={t('Try again')} onClick={() => setCatalogAttempt((n) => n + 1)} />
            </S.LoadMoreRow>
          </>
        ) : (
          <S.Note text="No games found." />
        )
      ) : null}

      <S.Grid>
        {shown.map((game) => {
          const key = gameKey(game);
          const isSelected = Boolean(selected[key]);
          const disabled = !isSelected && atMax;
          return (
            <S.GameCard
              key={key}
              type="button"
              $disabled={disabled}
              aria-pressed={isSelected}
              aria-disabled={disabled}
              onClick={() => {
                if (disabled) return; // handler self-gates the max (styling alone does not block a click)
                toggle(game);
              }}
            >
              <S.Thumb $selected={isSelected}>
                <GameThumb
                  game={game}
                  render={(src) => <S.ThumbImg src={src} alt="" loading="lazy" draggable={false} />}
                />
                {isSelected ? (
                  <S.Check aria-hidden="true">
                    <Icon name="check" size={13} />
                  </S.Check>
                ) : null}
              </S.Thumb>
              <S.GameName text={gameTitle(game)} />
            </S.GameCard>
          );
        })}
      </S.Grid>

      {visible < filtered.length ? (
        <S.LoadMoreRow>
          <Button variant="outline" label={t('Load more')} onClick={() => setVisible((v) => v + PAGE)} />
        </S.LoadMoreRow>
      ) : null}
    </Sheet>
  );
};

export default GamePickerSheet;
